import { LocationMetricRow } from './types'
import { formatNumber } from './utils'

export const downloadCSV = (
  sortedData: LocationMetricRow[],
  formattedEndDate: string,
  mainLocationCount: number,
  otherLocationCounts: number[],
  startDate?: string,
): void => {
  // Determine if we should use new metrics (after 2026-04-04)
  const useNewMetrics = startDate && startDate >= '2026-04-04'

  // CSV header row
  const headers = [
    'Store',
    'Attainment',
    'QTD Sales',
    'Average Weekly Sales',
    'Weekly Sales to Next Band',
    ...(useNewMetrics
      ? ['Large Meat Variance', 'Labor Variance']
      : [
          'Food Variance',
          'Paper Variance',
          'Labor Variance',
          'Total Variance',
        ]),
    'OSAT',
    'Google',
    'Delivery',
    ...(useNewMetrics
      ? ['SOS (HME)', 'SOS (Qualtrics DT)']
      : ['SOS Attainment']),
    'OP Prime Cost Bonus',
    'OP Guest Bonus',
    'OP Total Bonus',
    'OP Missed Bonus',
    'Manager Prime Cost Bonus',
    'Manager Guest Bonus',
    'Manager Total Bonus',
    'Manager Missed Bonus',
  ].join(',')

  // Format data rows
  const csvRows = sortedData.map((row, index) => {
    // Determine location count for averaging
    // Individual stores: use 1 (no averaging)
    // Summary row for main dataset (-1): use mainLocationCount
    // Summary rows for other market leaders: use corresponding counts from otherLocationCounts
    let locationCount = 1
    // Use Number() to ensure type coercion for comparison
    const locId = Number(row.locationId)
    if (locId === -1) {
      locationCount = mainLocationCount
    } else if (locId < -1) {
      // For other market leaders, locationId is negative (-3, -5, -7, etc.)
      // Calculate index: -3 -> 0, -5 -> 1, -7 -> 2, etc.
      const otherIndex = Math.floor((Math.abs(locId) - 3) / 2)
      locationCount = otherLocationCounts[otherIndex] ?? 1
    }
    const primeCostColumns = useNewMetrics
      ? [
          row.foodVariance
            ? formatNumber(row.foodVariance.value, false, true, 2)
            : '-',
          row.laborVariance
            ? formatNumber(row.laborVariance.value, false, true, 2)
            : '-',
        ]
      : [
          row.foodVariance
            ? formatNumber(row.foodVariance.value, false, true, 2)
            : '-',
          row.paperVariance
            ? formatNumber(row.paperVariance.value, false, true, 2)
            : '-',
          row.laborVariance
            ? formatNumber(row.laborVariance.value, false, true, 2)
            : '-',
          row.totalVariance
            ? formatNumber(row.totalVariance.value, false, true, 2)
            : '-',
        ]

    const sosColumns = useNewMetrics
      ? [
          row.sosAttainment
            ? formatNumber(row.sosAttainment.value, false, true, 2)
            : '-',
          row.sosQualtricsAttainment
            ? formatNumber(row.sosQualtricsAttainment.value, false, true, 2)
            : '-',
        ]
      : [
          row.sosAttainment
            ? formatNumber(row.sosAttainment.value, false, true, 2)
            : '-',
        ]

    return [
      row.locationId < 0
        ? row.locationName
        : `"${row.locationCode} - ${row.locationName}"`,
      row.bonusAttainment
        ? formatNumber(row.bonusAttainment.value, false, true, 2)
        : '-',
      row.netSales ? row.netSales.value / 100 : '-',
      row.avgWeeklySales
        ? (row.avgWeeklySales.value / 100 / locationCount).toFixed(2)
        : '-',
      row.bonusSalesToNextBand
        ? (row.bonusSalesToNextBand.value / 100 / locationCount).toFixed(2)
        : '-',
      ...primeCostColumns,
      row.osat ? formatNumber(row.osat.value, false, true, 2) : '-',
      row.google ? formatNumber(row.google.value, false, false, 2) : '-',
      row.delivery ? formatNumber(row.delivery.value, false, false, 2) : '-',
      ...sosColumns,
      row.opPrimeCostBonus ? row.opPrimeCostBonus.value / 100 : '-',
      row.opGuestBonus ? row.opGuestBonus.value / 100 : '-',
      row.opTotalBonus ? row.opTotalBonus.value / 100 : '-',
      row.opMissedOpportunity ? row.opMissedOpportunity.value / 100 : '-',
      row.managerPrimeCostBonus
        ? row.managerPrimeCostBonus.value / 2 / 100
        : '-',
      row.managerGuestBonus ? row.managerGuestBonus.value / 2 / 100 : '-',
      row.managerTotalBonus ? row.managerTotalBonus.value / 2 / 100 : '-',
      row.managerMissedOpportunity
        ? row.managerMissedOpportunity.value / 2 / 100
        : '-',
    ].join(',')
  })

  // Combine header and rows
  const csvContent = [headers, ...csvRows].join('\n')

  // Create a Blob and download link
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  link.setAttribute(
    'download',
    `ops_scorecard_${formattedEndDate.replace(/\//g, '-')}.csv`,
  )
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
